// Unified onboarding dashboard. Single screen for view + edit. The journey
// is always rendered with the same canvas layout used by the editor; the
// "Editar jornada" toggle just flips edit affordances (drag handle, +Add,
// edit/remove icons) on and off. Replaces the old read-only dashboard, the
// "Ver como novo colaborador" preview route, and the /customize page for
// active onboardings.
//
// Drafts (pre-start onboardings) still go through /customize because they
// have their own lifecycle (discard, start) and UX.

import {
  MILESTONE_DAYS,
  type MilestoneDay,
  type OnboardingDiscipline,
  type OnboardingResource,
} from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { CanvasMilestonesPanel } from "@/components/canvas/canvas-milestones-panel";
import { useSnapshotMilestonesDataSource } from "@/components/canvas/use-snapshot-data-source";
import { WelcomeMessageEditor } from "@/components/canvas/welcome-message-editor";
import { ActivityFeed } from "@/components/onboardings/activity-feed";
import { ChangeBuddyDialog } from "@/components/onboardings/change-buddy-dialog";
import { FinalizeOnboardingDialog } from "@/components/onboardings/finalize-onboarding-dialog";
import { StatusPill } from "@/components/onboardings/status-pill";
import { PageHeader } from "@/components/page-header";
import { PreviewPortalProvider } from "@/components/portal/preview-context";
import { ResourcePreviewPane } from "@/components/preview/resource-preview-pane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { DISCIPLINE_LABELS } from "@/lib/discipline-labels";
import { orpc, rpcClient } from "@/lib/rpc";
import { PortalJourney } from "@/pages/portal/portal-journey";

const PREVIEW_ERROR_NO_MILESTONES = "milestone_not_found";

function formatStartDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dt);
}

function StatsStrip({
  progress,
  currentDay,
  daysRemaining,
  overdueCount,
}: {
  progress: { done: number; total: number; pct: number };
  currentDay: number;
  daysRemaining: number;
  overdueCount: number;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-10">
      <StatBlock
        label="Progresso da jornada"
        value={
          <span className="tabular-nums">
            {progress.done}
            <span className="text-muted-foreground/70 font-normal">/{progress.total}</span>
          </span>
        }
        hint={`${progress.pct}% concluído`}
        bar={
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
        }
        className="flex-1"
      />
      <StatBlock
        label="Dia"
        value={
          <span className="tabular-nums">
            {currentDay}
            <span className="text-muted-foreground/70 font-normal">/60</span>
          </span>
        }
        hint={daysRemaining > 0 ? `${daysRemaining} restantes` : "Encerrado"}
        className="shrink-0"
      />
      <StatBlock
        label="Atrasados"
        value={
          <span
            className={`tabular-nums ${overdueCount > 0 ? "text-destructive" : "text-muted-foreground/70"}`}
          >
            {overdueCount}
          </span>
        }
        hint={
          overdueCount === 0
            ? "nenhum prazo vencido"
            : overdueCount === 1
              ? "item com prazo vencido"
              : "itens com prazo vencido"
        }
        className="shrink-0"
      />
    </div>
  );
}

function StatBlock({
  label,
  value,
  hint,
  bar,
  className,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  bar?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-[11px] font-medium uppercase tracking-[0.5px] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold leading-none">{value}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
      {bar}
    </div>
  );
}

export default function OnboardingDashboardPage() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ?edit=1 — entry point for users coming from old /customize links.
  const initialEditing = new URLSearchParams(location.split("?")[1] ?? "").get("edit") === "1";

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [changeBuddyOpen, setChangeBuddyOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [selectedResource, setSelectedResource] = useState<OnboardingResource | null>(null);
  const [previewSelectedDay, setPreviewSelectedDay] = useState<number | null>(null);
  const [previewResourceId, setPreviewResourceId] = useState<string | null>(null);

  const dashboard = useQuery({
    ...orpc.onboardings.getById.queryOptions({ input: { id } }),
    enabled: !!id,
  });

  const currentDayForPreview = dashboard.data?.currentDay ?? 0;
  const defaultPreviewDay =
    [...MILESTONE_DAYS].reverse().find((day) => day <= currentDayForPreview) ?? MILESTONE_DAYS[0];
  const previewDay = previewSelectedDay ?? defaultPreviewDay;
  const previewQuery = useQuery({
    ...orpc.preview.getOnboardingPreview.queryOptions({
      input: { onboardingId: id ?? "", dayNumber: previewDay },
    }),
    enabled: !!id && !isEditing,
  });

  const milestonesDataSource = useSnapshotMilestonesDataSource(id ?? "", {
    isOwner: !!(dashboard.data?.viewer?.canEdit ?? false),
    templateId: dashboard.data?.onboarding?.templateId ?? null,
  });

  const updateWelcomeMut = useMutation({
    mutationFn: (welcomeMessages: Record<string, string>) =>
      rpcClient.onboardings.updateWelcome({ id: id ?? "", welcomeMessages }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.getById.queryOptions({ input: { id: id ?? "" } }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.preview.getOnboardingPreview.queryOptions().queryKey,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Não foi possível salvar a mensagem do marco",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  if (dashboard.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (dashboard.isError || !dashboard.data) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/onboardings")} size="sm">
          <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
        </Button>
        <p className="mt-4 text-sm text-destructive">Onboarding não encontrado.</p>
      </div>
    );
  }

  const d = dashboard.data;
  const o = d.onboarding;
  const isActive = o.state === "active";
  const canEdit = d.viewer.canEdit && isActive;
  const canManage = d.viewer.canManage && isActive;
  const editAffordancesOn = canEdit && isEditing;

  const overdueTodoCount = d.journeyItems.filter(
    (i) => !i.done && i.milestoneDay !== null && i.milestoneDay < d.currentDay,
  ).length;

  const overrides: Record<string, string> = o.welcomeMessages ?? {};
  const templateMessages: Record<string, string> = d.templateWelcomeMessages ?? {};

  const renderWelcomeEditor = (day: MilestoneDay) => {
    const key = String(day);
    const hasOverride = Object.hasOwn(overrides, key);
    return (
      <WelcomeMessageEditor
        milestoneDay={day}
        value={overrides[key] ?? ""}
        inheritedValue={templateMessages[key] ?? ""}
        inheriting={!hasOverride}
        variant="override"
        overrideBadgeLabel={o.hireFullName}
        isOwner={editAffordancesOn}
        onCommit={(next) => {
          const merged = { ...overrides };
          if (next === "") delete merged[key];
          else merged[key] = next;
          updateWelcomeMut.mutate(merged);
        }}
        onRestoreOriginal={() => {
          const merged = { ...overrides };
          delete merged[key];
          updateWelcomeMut.mutate(merged);
        }}
      />
    );
  };

  const toggleEdit = () => {
    setSelectedResource(null);
    setIsEditing((v) => !v);
  };

  return (
    <div className="p-6">
      <Button variant="ghost" onClick={() => navigate("/onboardings")} size="sm" className="mb-3">
        <ArrowLeft className="mr-1 h-4 w-4" /> Onboardings ativos
      </Button>

      <PageHeader title={o.hireFullName}>
        <div className="flex items-center gap-3">
          {canEdit && (
            <div className="flex items-center gap-2">
              <Switch
                id="edit-journey-toggle"
                checked={isEditing}
                onCheckedChange={() => toggleEdit()}
                data-testid="button-edit-journey"
              />
              <Label htmlFor="edit-journey-toggle" className="cursor-pointer text-sm font-medium">
                Modo edição
              </Label>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Mais ações"
                data-testid="button-onboarding-actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => setActivityOpen(true)}
                data-testid="button-show-activity"
              >
                Ver atividade
              </DropdownMenuItem>
              {canManage && (
                <>
                  <DropdownMenuItem
                    onSelect={() => setChangeBuddyOpen(true)}
                    data-testid="button-change-buddy"
                  >
                    Trocar buddy
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => setFinalizeOpen(true)}
                    data-testid="button-finalize-onboarding"
                  >
                    Finalizar onboarding
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PageHeader>

      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <StatusPill value={d.statusPill} />
        <span>
          {DISCIPLINE_LABELS[o.discipline as OnboardingDiscipline] ?? o.discipline} · {o.level}
        </span>
        <span>·</span>
        <span>Início em {formatStartDate(o.startDate)}</span>
        {o.state !== "active" && (
          <Badge variant={o.state === "graduated" ? "default" : "secondary"}>{o.state}</Badge>
        )}
      </div>

      <div className="mt-4">
        <StatsStrip
          progress={d.journeyProgress}
          currentDay={d.currentDay}
          daysRemaining={d.daysRemaining}
          overdueCount={overdueTodoCount}
        />
      </div>

      <div className="mt-8">
        {isEditing ? (
          selectedResource ? (
            <ResourcePreviewPane
              templateId={o.templateId}
              resourceId={selectedResource.id}
              onBack={() => setSelectedResource(null)}
            />
          ) : (
            <CanvasMilestonesPanel
              dataSource={milestonesDataSource}
              isOwner={editAffordancesOn}
              onResourceClick={setSelectedResource}
              hideWelcomeHeader
              renderWelcomeForDay={(day: MilestoneDay) => renderWelcomeEditor(day)}
            />
          )
        ) : previewResourceId ? (
          <ResourcePreviewPane
            templateId={o.templateId}
            resourceId={previewResourceId}
            onBack={() => setPreviewResourceId(null)}
          />
        ) : previewQuery.isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (previewQuery.error as any)?.data?.code === PREVIEW_ERROR_NO_MILESTONES ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-sm text-muted-foreground">
              Nenhum marco configurado para este template.
            </p>
          </div>
        ) : previewQuery.isError || !previewQuery.data ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-sm text-muted-foreground">Não foi possível carregar o preview.</p>
          </div>
        ) : (
          <PreviewPortalProvider
            value={{
              templateId: previewQuery.data.templateId,
              availableDays: previewQuery.data.availableDays,
              today: previewQuery.data.today,
              milestone: previewQuery.data.milestone,
              sidebar: previewQuery.data.sidebar,
              milestoneSelected: previewDay,
              setMilestoneSelected: (day) => {
                setPreviewResourceId(null);
                setPreviewSelectedDay(day);
              },
              onResourceNavigate: (resourceId) => setPreviewResourceId(resourceId),
            }}
          >
            <PortalJourney />
          </PreviewPortalProvider>
        )}
      </div>

      <Sheet open={activityOpen} onOpenChange={setActivityOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Atividade</SheetTitle>
          </SheetHeader>
          <ActivityFeed entries={d.activity} />
        </SheetContent>
      </Sheet>

      <FinalizeOnboardingDialog
        open={finalizeOpen}
        onOpenChange={setFinalizeOpen}
        onboardingId={o.id}
      />
      <ChangeBuddyDialog
        open={changeBuddyOpen}
        onOpenChange={setChangeBuddyOpen}
        onboardingId={o.id}
        currentBuddyId={d.buddy?.id ?? null}
      />
    </div>
  );
}
